import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { motion } from 'framer-motion';
import { Phone } from 'lucide-react';

const LiveCallsPanel = ({ reducedMotion = false }) => {
  const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';
  const [liveCalls, setLiveCalls] = useState([]);

  useEffect(() => {
    // Mock live calls (would use WebSocket in production)
    const mockCalls = [
      { id: '1', status: 'connected', phone: '+1-555-0101', duration: 45, intent: 'question' },
      { id: '2', status: 'ringing', phone: '+1-555-0102', duration: 8, intent: null },
      { id: '3', status: 'analyzing', phone: '+1-555-0103', duration: 67, intent: 'interested' },
      { id: '4', status: 'dialing', phone: '+1-555-0104', duration: 2, intent: null }
    ];

    setLiveCalls(mockCalls);

    // Simulate updates
    const interval = setInterval(() => {
      setLiveCalls(prev => prev.map(call => ({
        ...call,
        duration: call.duration + 1
      })));
    }, 1000);

    return () => clearInterval(interval);
  }, []);

  const getStatusColor = (status) => {
    switch (status) {
      case 'dialing':
        return '#6b7280';
      case 'ringing':
        return '#f59e0b';
      case 'connected':
        return '#10b981';
      case 'analyzing':
        return '#C9A24A';
      default:
        return '#6b7280';
    }
  };

  const formatDuration = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const Waveform = ({ color }) => {
    return (
      <div className="flex items-center gap-0.5 h-6">
        {[0, 1, 2, 3, 4].map((i) => (
          <motion.div
            key={i}
            className="w-1 rounded-full"
            style={{ backgroundColor: color }}
            animate={{
              height: ['20%', '100%', '40%', '80%', '20%']
            }}
            transition={{
              duration: 0.8,
              repeat: Infinity,
              delay: i * 0.1
            }}
          />
        ))}
      </div>
    );
  };

  return (
    <Card className="bg-slate-900/50 border-slate-800">
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-lg text-slate-100">
          <Phone className="w-5 h-5 text-green-400" />
          Live Calls
        </CardTitle>
        <p className="text-xs text-slate-400 mt-1">
          Real-time call activity • Updates every second
        </p>
      </CardHeader>
      <CardContent>
        {liveCalls.length === 0 ? (
          <div className="text-center py-12 text-slate-400">
            <p>No active calls at the moment</p>
          </div>
        ) : (
          <div className="space-y-3">
            {liveCalls.map((call, index) => (
              <motion.div
                key={call.id}
                className="bg-slate-800/50 rounded-lg p-4 border border-slate-700 hover:border-gold/400 transition-colors"
                initial={reducedMotion ? {} : { opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.3, delay: index * 0.05 }}
              >
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    {/* Status Badge */}
                    <div
                      className="px-3 py-1 rounded-full text-xs font-medium flex items-center gap-2"
                      style={{
                        backgroundColor: getStatusColor(call.status) + '20',
                        color: getStatusColor(call.status)
                      }}
                    >
                      <div
                        className="w-2 h-2 rounded-full"
                        style={{ backgroundColor: getStatusColor(call.status) }}
                      />
                      {call.status}
                    </div>

                    {/* Phone Number */}
                    <span className="text-sm text-slate-300 font-mono">{call.phone}</span>
                  </div>

                  <div className="flex items-center gap-4">
                    {/* Intent Badge */}
                    {call.intent && (
                      <span className="text-xs text-slate-400 bg-slate-700/50 px-2 py-1 rounded">
                        {call.intent}
                      </span>
                    )}

                    {/* Duration */}
                    <span className="text-sm font-mono text-gold">
                      {formatDuration(call.duration)}
                    </span>

                    {/* Waveform (only for connected/analyzing) */}
                    {(call.status === 'connected' || call.status === 'analyzing') && (
                      <Waveform color={getStatusColor(call.status)} />
                    )}
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        )}

        <div className="mt-6 text-center">
          <p className="text-xs text-slate-500">
            {liveCalls.length} active {liveCalls.length === 1 ? 'call' : 'calls'}
          </p>
        </div>
      </CardContent>
    </Card>
  );
};

export default LiveCallsPanel;
